import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Star, 
  Trash2, 
  Save,
  Plus,
  GripVertical,
  MapPin,
  Search,
  Loader2,
  Camera
} from 'lucide-react';
import { placesAPI, schoolAPI, campusAPI } from '../api';
import { Place, PopularPlace, Campus, School } from '../types';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Sortable Place Item Component
interface SortableItemProps {
  id: string;
  place: Place;
  onRemove: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, place, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border ${
        isDragging ? 'border-primary shadow-lg' : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-move">
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span className="font-medium">{place.name}</span>
        </div>
        {place.address && (
          <p className="text-sm text-gray-500 mt-1">{place.address}</p>
        )}
        <div className="flex items-center gap-2 mt-2">
          {place.rating && (
            <Badge variant="secondary">
              <Star className="h-3 w-3 mr-1" />
              {place.rating}
            </Badge>
          )}
          {place.user_ratings_total && (
            <Badge variant="outline">
              {place.user_ratings_total} reviews
            </Badge>
          )}
        </div>
      </div>
      
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(id)}
        className="text-red-500 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const PopularPlacesPage: React.FC = () => {
  const [popularPlaces, setPopularPlaces] = useState<PopularPlace[]>([]);
  const [availablePlaces, setAvailablePlaces] = useState<Place[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [selectedCampus, setSelectedCampus] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { showNotification } = useNotification();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch schools and campuses on mount
  useEffect(() => {
    const init = async () => {
      await fetchSchools();
      await fetchCampuses();
    };
    init();
  }, []);

  // Fetch popular places when school/campus changes
  useEffect(() => {
    if (selectedSchool && selectedCampus) {
      fetchPopularPlaces();
    }
  }, [selectedSchool, selectedCampus]);

  // Clear search when dialog closes
  useEffect(() => {
    if (!isAddDialogOpen) {
      setSearchTerm('');
      setAvailablePlaces([]);
    }
  }, [isAddDialogOpen]);

  // Debug: Log when availablePlaces changes
  useEffect(() => {
    console.log('Available places updated:', availablePlaces.length, 'places');
    if (availablePlaces.length > 0) {
      console.log('First place:', availablePlaces[0]);
    }
  }, [availablePlaces]);

  const fetchSchools = async () => {
    try {
      const data = await schoolAPI.getAllSchools();
      setSchools(data);
      if (data.length > 0 && !selectedSchool) {
        setSelectedSchool(data[0]._id);
      }
    } catch (err) {
      console.error('Failed to fetch schools:', err);
      showNotification('Failed to fetch schools', 'error');
    }
  };

  const fetchCampuses = async () => {
    try {
      const data = await campusAPI.getAllCampuses();
      // Handle both array and object response
      const campusList = Array.isArray(data) ? data : (data?.data || []);
      console.log('Fetched all campuses:', campusList);
      setCampuses(campusList);
      
      // Auto-select first campus if available and none selected
      if (campusList.length > 0 && !selectedCampus) {
        setSelectedCampus(campusList[0]._id);
      }
    } catch (err) {
      console.error('Failed to fetch campuses:', err);
      setCampuses([]);
    }
  };


  const fetchPopularPlaces = async () => {
    if (!selectedSchool || !selectedCampus) {
      setPopularPlaces([]);
      return;
    }
    
    try {
      setLoading(true);
      const data = await placesAPI.getPopularPlaces(selectedSchool, selectedCampus);
      setPopularPlaces(data);
      setError(null);
      setHasChanges(false);
    } catch (err) {
      setError('Failed to fetch popular places');
      console.error('Error fetching popular places:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchPlaces = async () => {
    console.log('Search places called with:');
    console.log('- selectedCampus:', selectedCampus);
    console.log('- searchTerm:', searchTerm);
    
    if (!selectedCampus) {
      showNotification('Please select a campus first', 'warning');
      console.warn('Search blocked: No campus selected');
      return;
    }

    if (!searchTerm.trim()) {
      showNotification('Please enter a search term', 'warning');
      return;
    }

    try {
      setSearchingPlaces(true);
      setAvailablePlaces([]); // Clear previous results
      
      // Fetch ALL places for the campus (no limit or high limit)
      console.log('Fetching all places for campus:', selectedCampus);
      
      const response = await placesAPI.getAllPlaces({
        campus_id: selectedCampus,
        limit: 1000, // Get all places (or a very high limit)
        is_deleted: false,
      });
      
      console.log('API Response - Total places:', response.total);
      
      if (response && response.places) {
        // Client-side filtering since backend doesn't support search
        const searchLower = searchTerm.toLowerCase().trim();
        const filteredPlaces = response.places.filter(place => {
          // Search in name, address, and types
          const nameMatch = place.name?.toLowerCase().includes(searchLower);
          const addressMatch = place.address?.toLowerCase().includes(searchLower) || 
                              place.formatted_address?.toLowerCase().includes(searchLower);
          const typesMatch = place.google_types?.some(type => 
            type.toLowerCase().includes(searchLower)
          );
          
          return nameMatch || addressMatch || typesMatch;
        });
        
        console.log(`Filtered ${filteredPlaces.length} places from ${response.places.length} total`);
        setAvailablePlaces(filteredPlaces);
        
        if (filteredPlaces.length === 0) {
          showNotification('No places found matching your search', 'info');
        } else {
          console.log(`Found ${filteredPlaces.length} places matching "${searchTerm}"`);
        }
      } else {
        console.warn('Unexpected response structure:', response);
        setAvailablePlaces([]);
      }
    } catch (err) {
      console.error('Failed to search places - Full error:', err);
      showNotification('Failed to search places', 'error');
      setAvailablePlaces([]);
    } finally {
      setSearchingPlaces(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setPopularPlaces((items) => {
        const oldIndex = items.findIndex((item) => 
          (item.place_id as Place)._id === active.id
        );
        const newIndex = items.findIndex((item) => 
          (item.place_id as Place)._id === over?.id
        );

        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order numbers
        return newItems.map((item, index) => ({
          ...item,
          order: index,
        }));
      });
      setHasChanges(true);
    }
  };

  const handleAddPlace = (place: Place) => {
    // Check if place already exists
    const exists = popularPlaces.some(pp => 
      (pp.place_id as Place)._id === place._id
    );
    
    if (exists) {
      showNotification('This place is already in the popular places list', 'warning');
      return;
    }

    const newPopularPlace: PopularPlace = {
      _id: `temp-${Date.now()}`, // Temporary ID
      school_id: selectedSchool,
      campus_id: selectedCampus,
      place_id: place,
      order: popularPlaces.length,
      is_active: true,
      added_at: new Date(),
      updated_at: new Date(),
    };

    setPopularPlaces([...popularPlaces, newPopularPlace]);
    setHasChanges(true);
    setIsAddDialogOpen(false);
    setSearchTerm('');
    setAvailablePlaces([]);
  };

  const handleRemovePlace = (id: string) => {
    setPopularPlaces(places => {
      const filtered = places.filter(pp => 
        (pp.place_id as Place)._id !== id
      );
      // Reorder remaining places
      return filtered.map((item, index) => ({
        ...item,
        order: index,
      }));
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const placesToSave = popularPlaces.map((pp, index) => ({
        place_id: typeof pp.place_id === 'string' ? pp.place_id : (pp.place_id as Place)._id,
        order: index,
      }));

      await placesAPI.setPopularPlaces(
        selectedSchool,
        placesToSave,
        selectedCampus || undefined
      );

      showNotification('Popular places saved successfully', 'success');
      setHasChanges(false);
      fetchPopularPlaces(); // Refresh the list
    } catch (err) {
      console.error('Failed to save popular places:', err);
      showNotification('Failed to save popular places', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Popular Places"
          description="Manage and order popular places for each campus"
        />

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Select Location</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>School</Label>
                <Select 
                  value={selectedSchool} 
                  onValueChange={(value) => {
                    setSelectedSchool(value);
                    // Reset campus when school changes
                    setSelectedCampus('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a school" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map((school) => (
                      <SelectItem key={school._id} value={school._id}>
                        {school.display_name || school.school_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Campus</Label>
                <Select value={selectedCampus || ''} onValueChange={setSelectedCampus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a campus" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      if (campuses.length === 0) {
                        return (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            No campuses available
                          </div>
                        );
                      }
                      
                      const filteredCampuses = campuses.filter((campus) => {
                        // If a school is selected, only show campuses for that school
                        if (selectedSchool) {
                          return campus.school_id === selectedSchool;
                        }
                        // Otherwise show all campuses
                        return true;
                      });
                      
                      if (filteredCampuses.length === 0) {
                        return (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            No campuses for selected school
                          </div>
                        );
                      }
                      
                      return filteredCampuses.map((campus) => {
                        // Find the school name for this campus
                        const school = schools.find(s => s._id === campus.school_id);
                        return (
                          <SelectItem key={campus._id} value={campus._id}>
                            {campus.campus_name}
                            {!selectedSchool && school && ` (${school.display_name || school.school_name})`}
                          </SelectItem>
                        );
                      });
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Popular Places List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Popular Places ({popularPlaces.length})
                {hasChanges && (
                  <Badge variant="destructive" className="ml-2">
                    Unsaved Changes
                  </Badge>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(true)}
                  disabled={!selectedCampus}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Place
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedCampus ? (
              <div className="text-center py-8 text-gray-500">
                Please select a campus to manage popular places
              </div>
            ) : popularPlaces.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No popular places configured for this campus
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={popularPlaces.map(pp => (pp.place_id as Place)._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {popularPlaces.map((popularPlace) => {
                      const place = popularPlace.place_id as Place;
                      return (
                        <SortableItem
                          key={place._id}
                          id={place._id}
                          place={place}
                          onRemove={handleRemovePlace}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Place Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh]">
          <DialogHeader>
            <DialogTitle>Add Popular Place</DialogTitle>
            <DialogDescription>
              {selectedCampus && campuses.find(c => c._id === selectedCampus) ? (
                <>
                  Search and select a place from <strong>{campuses.find(c => c._id === selectedCampus)?.campus_name}</strong> to add to the popular places list
                </>
              ) : (
                'Search and select a place to add to the popular places list'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col h-[calc(95vh-8rem)] space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search places..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchPlaces()}
              />
              <Button onClick={searchPlaces} disabled={searchingPlaces}>
                {searchingPlaces ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-2">
              {searchingPlaces ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Searching places...</p>
                </div>
              ) : availablePlaces.length === 0 && searchTerm ? (
                <div className="text-center py-8">
                  <MapPin className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">No places found for "{searchTerm}"</p>
                </div>
              ) : availablePlaces.length === 0 ? (
                <div className="text-center py-8">
                  <Search className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Search for places to add</p>
                </div>
              ) : (
                availablePlaces.map((place) => {
                  // Check if place is already in popular places
                  const isAlreadyAdded = popularPlaces.some(pp => 
                    (pp.place_id as Place)._id === place._id
                  );
                  
                  return (
                    <Card
                      key={place._id}
                      className={`cursor-pointer transition-all ${
                        isAlreadyAdded 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md'
                      }`}
                      onClick={() => !isAlreadyAdded && handleAddPlace(place)}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {/* Photo Preview */}
                          <div className="flex-shrink-0">
                            {place.photo_info?.thumbnails?.[0]?.thumb_url || 
                             place.photos?.[0]?.optimized_urls?.thumb || 
                             place.photos?.[0]?.photo_url ? (
                              <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                                <img
                                  src={
                                    place.photo_info?.thumbnails?.[0]?.thumb_url || 
                                    place.photos?.[0]?.optimized_urls?.thumb || 
                                    place.photos?.[0]?.photo_url
                                  }
                                  alt={place.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    if (target.parentElement) {
                                      target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                          </div>

                          {/* Place Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-base truncate">
                                  {place.name}
                                </h4>
                                {place.address && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                    {place.address}
                                  </p>
                                )}
                                {place.google_types && place.google_types.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {place.google_types.slice(0, 3).map((type, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {type.replace(/_/g, ' ')}
                                      </Badge>
                                    ))}
                                    {place.google_types.length > 3 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{place.google_types.length - 3}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Rating and Status */}
                              <div className="flex flex-col items-end gap-2">
                                {place.rating && (
                                  <Badge variant="secondary" className="whitespace-nowrap">
                                    <Star className="h-3 w-3 mr-1 fill-current" />
                                    {place.rating.toFixed(1)}
                                    {place.user_ratings_total && (
                                      <span className="ml-1 text-xs opacity-75">
                                        ({place.user_ratings_total})
                                      </span>
                                    )}
                                  </Badge>
                                )}
                                {isAlreadyAdded && (
                                  <Badge variant="outline" className="text-xs">
                                    Already Added
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};